import React, { useMemo } from 'react';

import { Box, ListItem, Tooltip, useTheme } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import Skeleton from 'app/shared/components/common/Skeleton';
import { treeViewSlots } from 'app/shared/components/common/Tree';
import PropType from 'prop-types';

import { SystemCategoryIdList } from 'constants/documentCategories';
import { getScrollStyles } from 'styles/scrollbar.styles';

import { DocumentTestIds } from '../document.testIds';

import { getStyles } from './DocumentsTreeView.styles';

const DocumentsTreeView = ({
  expanded,
  treeRef,
  onNodeToggle,
  isSkeleton,
  tree,
  isEmptyTreeItemVisible,
  renderTypesCallback,
  onDragLeave,
  onDragOver,
}) => {
  const theme = useTheme();
  const scrollClasses = getScrollStyles(theme);
  const classes = getStyles(theme);

  const treeItemSx = useMemo(
    () => ({
      ...classes.root,
      '& > .MuiTreeItem-content': classes.content,
      '& .MuiTreeItem-label': classes.label,
      '& .MuiTreeItem-iconContainer': classes.iconContainer,
      '& > .MuiTreeItem-groupTransition': classes.group,
    }),
    [classes],
  );

  return (
    <SimpleTreeView
      className="relative overflow-y-auto h-full"
      data-testid={DocumentTestIds.TREE}
      expandedItems={expanded}
      itemChildrenIndentation={0}
      ref={treeRef}
      slots={treeViewSlots}
      sx={scrollClasses.scroll}
      onExpandedItemsChange={onNodeToggle}
    >
      {isSkeleton && <Skeleton height={34} length={15} />}

      {!isSkeleton &&
        tree?.map(({ documentCategoryId: id, documentCategoryName: name, types }) => {
          if (SystemCategoryIdList.includes(id)) {
            return null;
          }

          const typesCount = types?.reduce((acc, type) => acc + type.types.length, 0);

          if (!typesCount && !isEmptyTreeItemVisible) {
            return null;
          }

          return (
            <TreeItem
              itemId={id}
              key={id}
              label={
                <ListItem
                  aria-label={name}
                  component="div"
                  data-testid={DocumentTestIds.getCategoryId(id)}
                  id={id}
                  key={id}
                  sx={classes.category}
                  onDragLeave={onDragLeave}
                  onDragOver={onDragOver}
                >
                  <Box sx={classes.nameContainer}>
                    <Tooltip title={name}>
                      <Box sx={classes.name}>{name}</Box>
                    </Tooltip>
                  </Box>
                  <Box sx={classes.counter}>{typesCount}</Box>
                </ListItem>
              }
              sx={treeItemSx}
            >
              {!!types.length && (
                <div data-testid={DocumentTestIds.getTypesContainerId(id)}>
                  {expanded.includes(id) && renderTypesCallback(types)}
                </div>
              )}
            </TreeItem>
          );
        })}
    </SimpleTreeView>
  );
};

DocumentsTreeView.propTypes = {
  expanded: PropType.array.isRequired,
  treeRef: PropType.object.isRequired,
  isSkeleton: PropType.bool.isRequired,
  tree: PropType.array.isRequired,
  isEmptyTreeItemVisible: PropType.bool.isRequired,
  renderTypesCallback: PropType.func.isRequired,
  onNodeToggle: PropType.func.isRequired,
  onDragLeave: PropType.func,
  onDragOver: PropType.func,
};

export default DocumentsTreeView;
